import {useCallback, useMemo, useRef} from 'react'
import {AtUri} from '@atproto/syntax'
import {moderatePost} from '@bsky/sdk/moderation'
import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query'

import {
  getActiveAppViewPreset,
  useCustomAppViewDid,
  useCustomAppViewUrl,
} from '#/state/preferences/custom-appview-did'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {
  isBlackskySearchUpstreamFailure,
  isSearchV2Unavailable,
} from '#/state/queries/search-fallback'
import {useAppviewClient, useSession} from '#/state/session'
import {type SearchFilters} from '#/screens/Search/searchParams'
import {app} from '#/lexicons'
import {
  appendFromMe,
  buildSearchPostsV1Params,
  buildSearchPostsV2Filters,
  extractSearchPostsParams,
} from './search-posts-params'
import {
  didOrHandleUriMatches,
  embedViewRecordToPostView,
  getEmbeddedPost,
} from './util'

/** Remember unsupported v2 endpoints per session client, including pagination. */
const legacySearchClients = new WeakSet<object>()
type SearchPage = app.bsky.feed.searchPostsV2.$OutputBody & {
  blueskySearch?: boolean
}
type SearchCursor = {cursor: string; blueskySearch?: boolean} | undefined

const searchPostsQueryKeyRoot = 'search-posts'
const searchPostsV2QueryKey = ({
  query,
  sort,
  filters,
}: {
  query: string
  sort?: string
  filters?: SearchFilters
}) => [searchPostsQueryKeyRoot, query, sort, filters]

export function useSearchPostsV2Query({
  query,
  sort,
  enabled,
  filters,
}: {
  query: string
  sort?: 'top' | 'latest'
  enabled?: boolean
  filters?: SearchFilters
}) {
  const client = useAppviewClient()
  const {currentAccount} = useSession()
  const [appViewDid] = useCustomAppViewDid()
  const [appViewUrl] = useCustomAppViewUrl()
  const moderationOpts = useModerationOpts()
  const selectArgs = useMemo(
    () => ({
      isSearchingSpecificUser:
        /from:(\w+)/.test(query) || !!filters?.author || filters?.from === 'me',
      moderationOpts,
    }),
    [query, filters?.author, filters?.from, moderationOpts],
  )
  const lastRun = useRef<{
    data: InfiniteData<app.bsky.feed.searchPostsV2.$OutputBody>
    args: typeof selectArgs
    result: InfiniteData<app.bsky.feed.searchPostsV2.$OutputBody>
  } | null>(null)

  return useInfiniteQuery<
    SearchPage,
    Error,
    InfiniteData<app.bsky.feed.searchPostsV2.$OutputBody>,
    QueryKey,
    SearchCursor
  >({
    queryKey: [
      ...searchPostsV2QueryKey({query, sort, filters}),
      appViewDid,
      appViewUrl,
      currentAccount?.did,
    ],
    queryFn: async ({pageParam}) => {
      /*
       * Operators embedded in the query string (e.g. for back-compat links) are
       * merged with the explicit structured filters from the advanced search
       * dialog; see buildSearchPostsV2Filters for how the two sources combine.
       */
      const {q, ...embedded} = extractSearchPostsParams(query)
      /*
       * The generated params brand format-constrained strings (uri,
       * at-identifier, language) as template-literal types. The builder emits
       * plain strings parsed from user input, which are runtime-identical, so
       * the brand is asserted rather than re-validated here.
       */
      const builtFilters = buildSearchPostsV2Filters(
        embedded,
        filters,
      ) as app.bsky.feed.searchPostsV2.$Params
      const finalQuery = appendFromMe(q, filters?.from === 'me')
      const legacyParams = {
        ...buildSearchPostsV1Params(finalQuery, builtFilters),
        sort,
        limit: 25,
        cursor: pageParam?.cursor,
      }
      const searchBluesky = async (): Promise<SearchPage> => {
        const data = await client.call(
          app.bsky.feed.searchPosts,
          legacyParams,
          {service: 'did:web:api.bsky.app#bsky_appview'},
        )
        return {...data, blueskySearch: true}
      }
      // Keep Bluesky cursors on the same endpoint for this result set.
      if (pageParam?.blueskySearch) return searchBluesky()
      if (!legacySearchClients.has(client)) {
        try {
          return await client.call(app.bsky.feed.searchPostsV2, {
            ...builtFilters,
            query: finalQuery,
            limit: 25,
            cursor: pageParam?.cursor,
            /*
             * v2 calls the recency sort 'recent'; the rest of the app still uses
             * the v1 'latest' label.
             */
            sort: sort === 'latest' ? 'recent' : sort,
            allTime: true,
          })
        } catch (error) {
          if (!isSearchV2Unavailable(error)) throw error
          legacySearchClients.add(client)
        }
      }
      try {
        return await client.call(app.bsky.feed.searchPosts, legacyParams)
      } catch (error) {
        if (
          pageParam?.cursor ||
          getActiveAppViewPreset(appViewDid, appViewUrl) !== 'blacksky' ||
          !isBlackskySearchUpstreamFailure(error)
        )
          throw error
        return searchBluesky()
      }
    },
    initialPageParam: undefined,
    getNextPageParam: lastPage =>
      lastPage.cursor
        ? {cursor: lastPage.cursor, blueskySearch: lastPage.blueskySearch}
        : undefined,
    enabled: enabled ?? !!moderationOpts,
    select: useCallback(
      (data: InfiniteData<app.bsky.feed.searchPostsV2.$OutputBody>) => {
        const {moderationOpts, isSearchingSpecificUser} = selectArgs

        /*
         * If a user applies the `from:<user>` filter, don't apply any
         * moderation. Note that if we add any more filtering logic below, we
         * may need to adjust this.
         */
        if (isSearchingSpecificUser) {
          return data
        }

        /*
         * Keep track of the last run and whether we can reuse some already
         * selected pages from there.
         */
        let reusedPages = []
        if (lastRun.current) {
          const {
            data: lastData,
            args: lastArgs,
            result: lastResult,
          } = lastRun.current
          let canReuse = true
          for (let key in selectArgs) {
            if (selectArgs.hasOwnProperty(key)) {
              if (
                (selectArgs as Record<string, unknown>)[key] !==
                (lastArgs as Record<string, unknown>)[key]
              ) {
                // Can't do reuse anything if any input has changed.
                canReuse = false
                break
              }
            }
          }
          if (canReuse) {
            for (let i = 0; i < data.pages.length; i++) {
              if (data.pages[i] && lastData.pages[i] === data.pages[i]) {
                reusedPages.push(lastResult.pages[i])
                continue
              }
              // Stop as soon as pages stop matching up.
              break
            }
          }
        }

        const result = {
          ...data,
          pages: [
            ...reusedPages,
            ...data.pages.slice(reusedPages.length).map(page => {
              return {
                ...page,
                posts: page.posts.filter(post => {
                  const mod = moderatePost(post, moderationOpts!)
                  return !mod.ui('contentList').filter
                }),
              }
            }),
          ],
        }

        lastRun.current = {data, result, args: selectArgs}

        return result
      },
      [selectArgs],
    ),
  })
}

export function* findAllPostsInQueryData(
  queryClient: QueryClient,
  uri: string,
): Generator<app.bsky.feed.defs.PostView, undefined> {
  const queryDatas = queryClient.getQueriesData<
    InfiniteData<app.bsky.feed.searchPostsV2.$OutputBody>
  >({
    queryKey: [searchPostsQueryKeyRoot],
  })
  const atUri = new AtUri(uri)

  for (const [_queryKey, queryData] of queryDatas) {
    if (!queryData?.pages) {
      continue
    }
    for (const page of queryData?.pages) {
      for (const post of page.posts) {
        if (didOrHandleUriMatches(atUri, post)) {
          yield post
        }

        const quotedPost = getEmbeddedPost(post.embed)
        if (quotedPost && didOrHandleUriMatches(atUri, quotedPost)) {
          yield embedViewRecordToPostView(quotedPost)
        }
      }
    }
  }
}
